import { useEffect, useState } from "react";

import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
} from "@mui/material";

import { useConfirm } from "material-ui-confirm";

import { usePatchReIdentificationModelSelectionMutation } from "services/streamingApi/caseManager.api";
import { ObjectDetectionModelName } from "types/ObjectDetectionModelName";
import { PatchReIdentificationModelSelectionRequest } from "types/PatchReIdentificationModelSelection";
import { ObjectDetectionModel } from "types/reId/ObjectDetectionModel";
import { ReIdentificationModelSelection } from "types/reId/ReIdentificationModelSelection";
import { EntityId } from "@reduxjs/toolkit";

export interface SelectObjectDetectionModelsDialogProps {
    configurationSetting: ReIdentificationModelSelection;
    closeDialog: () => void;
    dossierId: EntityId;
    objectDetectionModels: ObjectDetectionModel[];
}

export function SelectObjectDetectionModelsDialog(
    props: SelectObjectDetectionModelsDialogProps,
) {
    const [
        patchReIdentificationModelSelection,
        { isLoading: isSaving, isError: isSaveError },
    ] = usePatchReIdentificationModelSelectionMutation();

    const [selectedNames, setSelectedNames] = useState<
        ObjectDetectionModelName[]
    >([]);

    const confirm = useConfirm();

    const initialNames =
        props.configurationSetting.selectedObjectDetectionModelNames ?? [];

    useEffect(() => {
        setSelectedNames(initialNames);
    }, [initialNames]);

    const hasSelection = selectedNames.length > 0;
    const isChanged =
        selectedNames.length !== initialNames.length ||
        selectedNames.some((name) => !initialNames.includes(name));

    const toggleModel = (name: ObjectDetectionModelName) => {
        if (initialNames.includes(name)) return;

        setSelectedNames((current) =>
            current.includes(name)
                ? current.filter((n) => n !== name)
                : [...current, name],
        );
    };

    const handleSave = async () => {
        if (!isChanged) {
            props.closeDialog();
            return;
        }

        const { confirmed } = await confirm({
            title: "Change object detection models?",
            description:
                "Processing the videos in this dossier for each newly selected model can take a considerable amount of time. Are you sure you want to continue?",
        });

        if (!confirmed) return;

        const result = await patchReIdentificationModelSelection({
            id: props.configurationSetting.id,
            dossierId: props.dossierId,
            selectedObjectDetectionModelNames: selectedNames,
        } as PatchReIdentificationModelSelectionRequest);

        if (!("error" in result)) {
            props.closeDialog();
        }
    };

    return (
        <>
            <Dialog
                open={true}
                fullWidth={true}
                maxWidth={"sm"}
                onClose={(event: object, reason: string) => {
                    if (reason !== "backdropClick" && !isSaving) {
                        props.closeDialog();
                    }
                }}
            >
                <DialogTitle>Object detection models</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        Models already in use cannot be removed; you can only
                        add models.
                    </DialogContentText>

                    <FormControl
                        error={!hasSelection}
                        component="fieldset"
                        variant="standard"
                    >
                        <FormGroup>
                            {props.objectDetectionModels.map((model) => (
                                <FormControlLabel
                                    key={model.name}
                                    label={model.displayName}
                                    control={
                                        <Checkbox
                                            checked={selectedNames.includes(
                                                model.name,
                                            )}
                                            onChange={() =>
                                                toggleModel(model.name)
                                            }
                                            disabled={
                                                isSaving ||
                                                initialNames.includes(
                                                    model.name,
                                                )
                                            }
                                        />
                                    }
                                />
                            ))}
                        </FormGroup>
                        {!hasSelection && (
                            <FormHelperText>
                                Select at least one model.
                            </FormHelperText>
                        )}
                    </FormControl>
                    {isSaveError && (
                        <Alert
                            severity="error"
                            sx={{ mt: 2 }}
                        >
                            Failed to save the selected models.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={props.closeDialog}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!hasSelection || isSaving}
                        startIcon={
                            isSaving ? <CircularProgress size={16} /> : null
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
